import type { EventId } from '@ps/isp-web-services-types';
import { Size } from '@ps/web-core-enums';
import { useScreenSize } from '@ps/web-shared-components';
import type { IFrameComponent, IframeResizerProps } from 'iframe-resizer-react';
import IframeResizer from 'iframe-resizer-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { type StreamType } from 'widgets/sports-event-info-widget/types';
import { select } from '@swf/core/store';
import type { EventTypeId } from '@swf/sports/types/eventType';
import useIsEventTypeLiveDataEnabled from '@swf/sports/utils/useIsEventTypeLiveDataEnabled';

import LoginForm from '../LoginForm/LoginForm';

import styles from './BvpPlayer.scss';
import LoadingState from './components/LoadingState';

interface BvpPlayerProps extends IframeResizerProps {
  eventId: EventId;
  eventTypeId: EventTypeId;
  name: string;
  streamType?: StreamType;
}

interface GetIFrameSrcProps {
  origin: string;
  locale: string;
  eventId: number;
  streamType?: StreamType;
}

const getIframeSrc = ({ origin, locale, eventId, streamType }: GetIFrameSrcProps) =>
  `${origin}/sports/web/gbp/bvp/GetPlayer.do?tr=10&contentType=${streamType}&allowPopup=true&width=2040&locale=${locale}&eID=${eventId}`;

export const BvpPlayer = ({ eventId, name, eventTypeId, streamType, ...rest }: BvpPlayerProps) => {
  // if the sport is not Football or Tennis we need to account for additional scoreboard height
  // otherwise account for tabs only
  const isLiveDataDisplayed = useIsEventTypeLiveDataEnabled(eventTypeId);
  const additionalContentHeight = isLiveDataDisplayed ? 137 : 40;
  const { greaterThan } = useScreenSize();
  const initialHeight = (greaterThan(Size.XSmall) ? 352 : 236) + additionalContentHeight;
  const origin = useSelector(select.location.getOrigin);
  const locale = useSelector(select.locale.get);
  const initialised = useRef(false);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const [loading, setLoading] = useState(true);

  const onResized = ({ iframe, height }: { iframe: IFrameComponent; height: number }) => {
    if (initialised.current) return;

    if (height > 0) {
      iframe.style.removeProperty('min-height');
      initialised.current = true;
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    initialised.current = false;
  }, [streamType]);

  return streamType === 'vid' && !isAuthenticated ? (
    <LoginForm />
  ) : (
    <div className={styles.bvpPlayerContainer}>
      {loading && <LoadingState height={initialHeight} />}
      <IframeResizer
        {...rest}
        maxHeight={initialHeight}
        onResized={onResized}
        className={styles.bvpPlayerIframe}
        title={name}
        src={getIframeSrc({ origin, locale, eventId, streamType })}
        style={{ minHeight: initialised.current ? 0 : initialHeight }}
      />
    </div>
  );
};
